// Borrower service for fetching borrower information
import axios from './api/axios.config';
import { API_ENDPOINTS } from '../constants/api.constants';
import type { ApiResponse } from '../types/api.types';

export interface BorrowerResponse {
  id: string;
  fullName: string;
  email: string;
  phoneNumber: string;
  companyName: string;
  city: string;
  state: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface BorrowerSearchResponse {
  borrowers: BorrowerResponse[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

/**
 * Search borrowers that the authenticated staff member is authorized to use.
 * The backend applies role-specific authorization, including salesperson territory scope.
 */
export const searchBorrowers = async (
  search: string,
  limit = 20
): Promise<ApiResponse<BorrowerSearchResponse>> => {
  try {
    const response = await axios.get<ApiResponse<BorrowerSearchResponse>>(
      API_ENDPOINTS.USERS.BORROWERS,
      { params: { search, limit } }
    );
    return response as unknown as ApiResponse<BorrowerSearchResponse>;
  } catch (error) {
    console.error('Error searching borrowers:', error);
    throw error;
  }
};

/**
 * Get borrower by email using the /users/by-email endpoint
 */
export const getBorrowerByEmail = async (email: string): Promise<ApiResponse<BorrowerResponse>> => {
  try {
    const response = await axios.get<ApiResponse<BorrowerResponse>>(API_ENDPOINTS.USERS.BY_EMAIL, {
      params: { email },
    });
    return response as unknown as ApiResponse<BorrowerResponse>;
  } catch (error) {
    console.error('Error fetching borrower by email:', error);
    throw error;
  }
};

/**
 * Get borrower by ID using the /users/{id} endpoint
 */
export const getBorrowerById = async (id: string): Promise<ApiResponse<BorrowerResponse>> => {
  try {
    const response = await axios.get<ApiResponse<BorrowerResponse>>(API_ENDPOINTS.USERS.BY_ID(id));
    return response as unknown as ApiResponse<BorrowerResponse>;
  } catch (error) {
    console.error('Error fetching borrower by ID:', error);
    throw error;
  }
};

/** Update the authenticated borrower's explicit Shepherd's Finance representative. */
export const updateAssignedSalesperson = async (
  salespersonId: string | null
): Promise<ApiResponse<unknown>> => {
  try {
    const response = await axios.put<ApiResponse<unknown>>(
      API_ENDPOINTS.USERS.ASSIGNED_SALESPERSON,
      { salespersonId }
    );
    return response as unknown as ApiResponse<unknown>;
  } catch (error) {
    console.error('Error updating assigned salesperson:', error);
    throw error;
  }
};
